import { memo } from 'react';
import { Link } from 'react-router-dom';

import { Badge, Flex, Text } from '@mantine/core';

import { getReportName } from '@/utils/constants';
import formatDate, { formatUtcDate } from '@/utils/formatDate';
import { routes } from '@/routes';

import { MiniTxReport, ReportColor, SignatureStatus } from '@/types/transmissionReport';
import IconButton from '@/ui/IconButton';

import styles from './PatientsReportList.module.scss';

const PatientReportCard = memo(
  ({
    report,
    handleDownloadReport,
    handleActiveReport
  }: {
    report: MiniTxReport;
    handleDownloadReport: (report: MiniTxReport) => void;
    handleActiveReport: (report: number) => void;
  }) => {
    const dotColor =
      report.color === ReportColor.Red
        ? 'var(--mantine-color-red-6)'
        : report.color === ReportColor.Yellow
          ? 'var(--mantine-color-yellow-6)'
          : report.color === ReportColor.Green
            ? 'var(--mantine-color-teal-6)'
            : 'var(--mantine-color-gray-4)';

    const SignatureStatusBadge = () => {
      switch (report.signatureStatus) {
        case SignatureStatus.Signed:
        case SignatureStatus.SignatureNotRequired:
          return (
            <Badge color="green" variant="light" radius="sm" size="xs" tt="none">
              Complete
            </Badge>
          );
        case SignatureStatus.NonPractitionerSigned:
          return (
            <Badge color="sky" variant="light" radius="sm" size="xs" tt="none">
              Pending Sig
            </Badge>
          );
        case SignatureStatus.Dismissed:
          return (
            <Badge color="gray" variant="light" radius="sm" size="xs" tt="none">
              Dismissed
            </Badge>
          );
        case SignatureStatus.PendingReview:
          return (
            <Badge color="orange" variant="light" radius="sm" size="xs" tt="none">
              Pending Review
            </Badge>
          );
        default:
          return (
            <Badge color="gray" variant="light" radius="sm" size="xs" tt="none">
              Draft
            </Badge>
          );
      }
    };

    const reportName = getReportName(
      report?.deviceType,
      report?.transmissionType,
      report?.color,
      report?.reportType === 'HEART_FAILURE'
    );

    return (
      <Flex className={styles.patientReportCard} gap={4} justify={'space-between'} align={'center'}>
        <Flex gap={'sm'} align={'center'}>
          <Flex align={'center'} justify={'center'}>
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: dotColor,
                display: 'inline-block'
              }}
            />
          </Flex>
          <Flex direction={'column'} justify={'space-between'} gap={4}>
            <Flex justify="space-between" align="center" direction="row" w="100%">
              <Flex align="center" gap="xs">
                <Text fw={600} fz={14} c={'neutral.8'} lh={1}>
                  {reportName}
                </Text>
                <SignatureStatusBadge />
                {report?.isBillable && (
                  <Badge color="plum" variant="light" radius="sm" size="xs" tt="none">
                    Billable
                  </Badge>
                )}
              </Flex>
            </Flex>
            <Flex gap="md" align="center">
              <Text fz={12} c="plum.5" lh={1.2} fw={500}>
                Transmitted on{' '}
                {report?.transmissionDate
                  ? formatDate(report.transmissionDate, 'M/DD/YY, h:mm A')
                  : 'Unassigned'}
              </Text>
              <Text fz={12} c="plum.5" lh={1.2} fw={500}>
                Date of Service:{' '}
                {report?.dateOfService
                  ? formatUtcDate(report.dateOfService, 'M/DD/YY')
                  : 'Unassigned'}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex gap={'xs'} align={'center'}>
          {report.signatureStatus === SignatureStatus.Dismissed ? (
            <Flex align={'center'}>
              <IconButton
                icon="download"
                onClick={() => {
                  handleDownloadReport(report);
                }}
              />
              <IconButton
                icon="activate"
                onClick={() => {
                  handleActiveReport(report?.id);
                }}
              />
            </Flex>
          ) : (
            <Flex align={'center'}>
              <IconButton
                icon="download"
                onClick={() => {
                  handleDownloadReport(report);
                }}
              />
              {report.signatureStatus !== SignatureStatus.SignatureNotRequired && (
                <Link
                  to={routes.physician.transmissionReportDetail(report?.id.toString())}
                  target="_blank">
                  <IconButton icon="externalLink" />
                </Link>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  }
);

PatientReportCard.displayName = 'PatientReportCard';

export default PatientReportCard;
